import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  ArrowLeft, DollarSign, TrendingUp, TrendingDown, ChevronDown,
  Zap, Award, Clock, Coffee, Target,
} from 'lucide-react';

// ─── Mock Data ──────────────────────────────────────────────────────

interface EarningsDay {
  date: string;
  dayOfWeek: string;
  baseTips: number;
  upsellBonus: number;
  shiftHours: number;
  upsellAttempts: number;
  upsellSuccesses: number;
  drinkPromotions: number;
}

const EARNINGS_HISTORY: EarningsDay[] = [
  { date: 'Mar 14', dayOfWeek: 'Fri', baseTips: 187, upsellBonus: 34, shiftHours: 7.5, upsellAttempts: 12, upsellSuccesses: 8, drinkPromotions: 5 },
  { date: 'Mar 13', dayOfWeek: 'Thu', baseTips: 142, upsellBonus: 28, shiftHours: 6, upsellAttempts: 10, upsellSuccesses: 7, drinkPromotions: 4 },
  { date: 'Mar 12', dayOfWeek: 'Wed', baseTips: 98, upsellBonus: 15, shiftHours: 5, upsellAttempts: 7, upsellSuccesses: 4, drinkPromotions: 2 },
  { date: 'Mar 11', dayOfWeek: 'Tue', baseTips: 112, upsellBonus: 22, shiftHours: 6, upsellAttempts: 9, upsellSuccesses: 6, drinkPromotions: 3 },
  { date: 'Mar 10', dayOfWeek: 'Mon', baseTips: 76, upsellBonus: 8, shiftHours: 4.5, upsellAttempts: 5, upsellSuccesses: 2, drinkPromotions: 1 },
  { date: 'Mar 9', dayOfWeek: 'Sun', baseTips: 203, upsellBonus: 45, shiftHours: 8, upsellAttempts: 14, upsellSuccesses: 11, drinkPromotions: 6 },
  { date: 'Mar 8', dayOfWeek: 'Sat', baseTips: 221, upsellBonus: 52, shiftHours: 8.5, upsellAttempts: 16, upsellSuccesses: 13, drinkPromotions: 7 },
  { date: 'Mar 7', dayOfWeek: 'Fri', baseTips: 178, upsellBonus: 31, shiftHours: 7, upsellAttempts: 11, upsellSuccesses: 8, drinkPromotions: 4 },
  { date: 'Mar 6', dayOfWeek: 'Thu', baseTips: 134, upsellBonus: 19, shiftHours: 6, upsellAttempts: 8, upsellSuccesses: 5, drinkPromotions: 3 },
  { date: 'Mar 5', dayOfWeek: 'Wed', baseTips: 91, upsellBonus: 12, shiftHours: 5, upsellAttempts: 6, upsellSuccesses: 3, drinkPromotions: 2 },
];

const WEEKLY_TOTALS = {
  thisWeek: { tips: 615, bonus: 107, hours: 29 },
  lastWeek: { tips: 624, bonus: 114, hours: 34.5 },
};

const MONTHLY_EARNINGS = {
  current: { total: 2847, bonusPortion: 523, avgPerShift: 203 },
  previous: { total: 2344, bonusPortion: 401, avgPerShift: 178 },
};

// ─── Component ──────────────────────────────────────────────────────

const MyEarnings: React.FC = () => {
  const navigate = useNavigate();
  const [timeframe, setTimeframe] = useState<'week' | 'month'>('week');

  const monthChange = MONTHLY_EARNINGS.current.total - MONTHLY_EARNINGS.previous.total;
  const monthPct = Math.round((monthChange / MONTHLY_EARNINGS.previous.total) * 100);

  // Simple bar chart values
  const maxEarning = Math.max(...EARNINGS_HISTORY.map(d => d.baseTips + d.upsellBonus));

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-gradient-to-r from-emerald-600 to-teal-600">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <button onClick={() => navigate('/restaurant/mockups')} className="flex items-center gap-1.5 text-emerald-100 hover:text-white text-sm mb-4 transition-colors">
            <ArrowLeft className="w-4 h-4" /> Back to Hub
          </button>
          <h1 className="text-2xl font-bold text-white mb-1">My Earnings</h1>
          <p className="text-emerald-100 text-sm">See exactly how Bespoke puts more money in your pocket</p>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-6 space-y-6">
        {/* Big number hero */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
          <div className="flex items-start justify-between mb-6">
            <div>
              <p className="text-sm text-gray-500 mb-1">This Month's Total Earnings</p>
              <div className="flex items-baseline gap-3">
                <span className="text-4xl font-bold text-gray-900">${MONTHLY_EARNINGS.current.total.toLocaleString()}</span>
                <span className={`inline-flex items-center gap-1 text-sm font-medium ${monthChange >= 0 ? 'text-emerald-600' : 'text-red-600'}`}>
                  {monthChange >= 0 ? <TrendingUp className="w-4 h-4" /> : <TrendingDown className="w-4 h-4" />}
                  {monthChange >= 0 ? '+' : ''}{monthPct}% vs last month
                </span>
              </div>
            </div>
            <div className="w-12 h-12 rounded-xl bg-emerald-100 flex items-center justify-center">
              <DollarSign className="w-6 h-6 text-emerald-600" />
            </div>
          </div>

          {/* Breakdown bar */}
          <div className="mb-2">
            <div className="flex justify-between text-xs text-gray-500 mb-1">
              <span>Base Tips: ${(MONTHLY_EARNINGS.current.total - MONTHLY_EARNINGS.current.bonusPortion).toLocaleString()}</span>
              <span>Upsell Bonus: ${MONTHLY_EARNINGS.current.bonusPortion.toLocaleString()}</span>
            </div>
            <div className="h-3 bg-gray-100 rounded-full overflow-hidden flex">
              <div
                className="bg-emerald-500 rounded-l-full"
                style={{ width: `${((MONTHLY_EARNINGS.current.total - MONTHLY_EARNINGS.current.bonusPortion) / MONTHLY_EARNINGS.current.total) * 100}%` }}
              />
              <div
                className="bg-emerald-300"
                style={{ width: `${(MONTHLY_EARNINGS.current.bonusPortion / MONTHLY_EARNINGS.current.total) * 100}%` }}
              />
            </div>
          </div>
          <p className="text-xs text-gray-400">
            Your upsell bonuses account for <span className="font-medium text-emerald-600">{Math.round((MONTHLY_EARNINGS.current.bonusPortion / MONTHLY_EARNINGS.current.total) * 100)}%</span> of your earnings.
            That's ${MONTHLY_EARNINGS.current.bonusPortion - MONTHLY_EARNINGS.previous.bonusPortion} more than last month.
          </p>
        </div>

        {/* The pitch card */}
        <div className="bg-gradient-to-r from-emerald-50 to-teal-50 rounded-xl border border-emerald-200 p-5">
          <div className="flex items-start gap-3">
            <div className="w-8 h-8 rounded-lg bg-emerald-100 flex items-center justify-center flex-shrink-0 mt-0.5">
              <Zap className="w-4 h-4 text-emerald-600" />
            </div>
            <div>
              <h3 className="text-sm font-semibold text-emerald-800 mb-1">You're earning $80 more per shift than average</h3>
              <p className="text-xs text-emerald-700">
                Servers using Bespoke at your restaurant average <strong>$203/shift</strong> vs the non-Bespoke average of <strong>$123/shift</strong>.
                That's an extra <strong>$1,920/month</strong> just by doing what you're already doing — but better.
              </p>
            </div>
          </div>
        </div>

        {/* Stat cards */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          {[
            { label: 'Avg / Shift', value: `$${MONTHLY_EARNINGS.current.avgPerShift}`, icon: Clock, change: `+$${MONTHLY_EARNINGS.current.avgPerShift - MONTHLY_EARNINGS.previous.avgPerShift}` },
            { label: 'Upsell Rate', value: '67%', icon: Target, change: '+8%' },
            { label: 'Drink Promos', value: '37', icon: Coffee, change: '+12' },
            { label: 'Best Day', value: '$273', icon: Award, change: 'Mar 8' },
          ].map((s, i) => (
            <div key={i} className="bg-white rounded-lg border border-gray-200 p-4">
              <div className="flex items-center gap-2 mb-2">
                <s.icon className="w-4 h-4 text-gray-400" />
                <span className="text-xs text-gray-500">{s.label}</span>
              </div>
              <div className="text-xl font-bold text-gray-900">{s.value}</div>
              <div className="text-xs text-emerald-600 mt-0.5">{s.change}</div>
            </div>
          ))}
        </div>

        {/* Daily breakdown */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
          <div className="flex items-center justify-between mb-5">
            <h2 className="text-base font-semibold text-gray-900">Daily Breakdown</h2>
            <div className="flex items-center gap-1 bg-gray-100 rounded-lg p-0.5">
              {(['week', 'month'] as const).map((t) => (
                <button
                  key={t}
                  onClick={() => setTimeframe(t)}
                  className={`px-3 py-1 text-xs font-medium rounded-md transition-colors ${
                    timeframe === t ? 'bg-white shadow-sm text-gray-900' : 'text-gray-500 hover:text-gray-700'
                  }`}
                >
                  {t === 'week' ? 'This Week' : 'This Month'}
                </button>
              ))}
            </div>
          </div>

          {/* Bar chart */}
          <div className="space-y-2">
            {EARNINGS_HISTORY.slice(0, timeframe === 'week' ? 7 : 10).map((day) => {
              const total = day.baseTips + day.upsellBonus;
              const pct = (total / maxEarning) * 100;
              const bonusPct = (day.upsellBonus / total) * 100;
              return (
                <div key={day.date} className="flex items-center gap-3">
                  <div className="w-16 text-xs text-gray-500 flex-shrink-0">
                    <span className="font-medium">{day.dayOfWeek}</span> {day.date}
                  </div>
                  <div className="flex-1 h-7 bg-gray-50 rounded-md overflow-hidden flex items-center relative">
                    <div
                      className="h-full bg-emerald-500 rounded-l-md flex items-center"
                      style={{ width: `${pct * (1 - bonusPct / 100)}%` }}
                    />
                    <div
                      className="h-full bg-emerald-300"
                      style={{ width: `${pct * (bonusPct / 100)}%` }}
                    />
                    <span className="absolute right-2 text-xs font-medium text-gray-600">
                      ${total}
                    </span>
                  </div>
                  <div className="w-20 text-right text-xs text-gray-400 flex-shrink-0">
                    {day.upsellSuccesses}/{day.upsellAttempts} upsells
                  </div>
                </div>
              );
            })}
          </div>

          <div className="flex items-center gap-4 mt-4 pt-3 border-t border-gray-100">
            <div className="flex items-center gap-1.5 text-xs text-gray-500">
              <div className="w-3 h-3 bg-emerald-500 rounded-sm" /> Base Tips
            </div>
            <div className="flex items-center gap-1.5 text-xs text-gray-500">
              <div className="w-3 h-3 bg-emerald-300 rounded-sm" /> Upsell Bonus
            </div>
          </div>
        </div>

        {/* Motivation footer */}
        <div className="text-center py-6">
          <p className="text-sm text-gray-400 italic">
            "I used to think upselling was pushy. Now I see it as giving guests a better experience — and getting paid for it."
          </p>
          <p className="text-xs text-gray-300 mt-1">— Sarah C., SAJJ Mediterranean, SF</p>
        </div>
      </div>
    </div>
  );
};

export default MyEarnings;
